/**
 * Outbox rules (pure — unit tested). The durable outbox (outbox.ts) keeps calendar writes made
 * offline in the base Dexie DB ('kgr', table outbox) and sends them FIFO.
 *
 *  - Items are tagged with the user who made them; only the signed-in user's items are flushed.
 *  - Collapse: a new write with the same key as a queued one (same user) replaces its payload but
 *    keeps the EARLIEST queue position (seq), so writes to different objects keep their order.
 *    The op id changes, so a send of the old payload that is in flight does not delete the newer one.
 *  - Failures: network / 5xx / 408 / 429 → keep and retry later (queue stops, order preserved);
 *    401 → stop (the auth layer routes to sign-in; items stay for the same user);
 *    any other 4xx → drop the item, report it, continue with the next one.
 */
import { ApiFailure, NetworkFailure } from '../api/client';
import type { OutboxItem } from '../db/dexie';

export type FailureAction = 'retry' | 'stop' | 'drop';

export function classifyFailure(e: unknown): FailureAction {
  if (e instanceof NetworkFailure) return 'retry';
  if (e instanceof ApiFailure) {
    if (e.status === 401) return 'stop';
    if (e.status >= 500 || e.status === 408 || e.status === 429) return 'retry';
    return 'drop';
  }
  // Anything else is a client bug or an unreadable success response: never let it block the queue.
  return 'drop';
}

/** Retry delay after `attempt` consecutive failures (1 → 5 s … capped at 60 s). */
export function retryDelayMs(attempt: number): number {
  const steps = [5_000, 10_000, 20_000, 40_000, 60_000];
  return steps[Math.min(Math.max(attempt, 1), steps.length) - 1]!;
}

/** The signed-in user's items of the given kinds, in FIFO (seq) order. */
export function flushOrder<T extends Pick<OutboxItem, 'seq' | 'userId' | 'kind'>>(items: readonly T[], userId: string, kinds: ReadonlySet<string>): T[] {
  return items.filter((i) => i.userId === userId && kinds.has(i.kind)).sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
}

export interface IncomingWrite {
  id: string;
  userId: string;
  kind: string;
  key: string;
  method: OutboxItem['method'];
  path: string;
  body: unknown;
  createdAt: number;
}

export type CollapsePlan =
  | { action: 'add'; item: Omit<OutboxItem, 'seq'> }
  | { action: 'replace'; seq: number; previousId: string; item: OutboxItem };

/**
 * Where an incoming write goes: replaces the payload of a queued item with the same user + key
 * (keeping its seq and createdAt), else appended. `merge` may combine the old and new bodies.
 */
export function planCollapse(queue: readonly OutboxItem[], w: IncomingWrite, merge?: (prev: unknown, next: unknown) => unknown): CollapsePlan {
  const prev = queue
    .filter((i) => i.userId === w.userId && i.key === w.key && i.seq !== undefined)
    .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))[0];
  if (!prev) {
    return {
      action: 'add',
      item: { id: w.id, userId: w.userId, kind: w.kind, key: w.key, method: w.method, path: w.path, body: w.body, createdAt: w.createdAt, attempts: 0, failed: null },
    };
  }
  return {
    action: 'replace',
    seq: prev.seq!,
    previousId: prev.id,
    item: {
      ...prev,
      id: w.id,
      kind: w.kind,
      method: w.method,
      path: w.path,
      body: merge ? merge(prev.body, w.body) : w.body,
      attempts: 0,
      failed: null,
    },
  };
}

/** Applies a collapse plan to an in-memory queue (used by tests and the in-memory mirror). */
export function applyCollapse(queue: readonly OutboxItem[], plan: CollapsePlan, nextSeq: number): OutboxItem[] {
  if (plan.action === 'add') return [...queue, { ...plan.item, seq: nextSeq }];
  return queue.map((i) => (i.seq === plan.seq ? plan.item : i));
}

/** A check-in sent within this time of the tap, on its first attempt, is a live one. */
export const LIVE_CHECKIN_MS = 5_000;

/**
 * Body of a queued self check-in. Only the first attempt made right at the tap is sent live ({},
 * server time). Any later attempt — queued offline, or a retry after a network blip — carries
 * estServerTime (the estimated server time of the tap), so a tap just before the window closes is
 * still accepted as 'self-offline' when the retry arrives after it.
 */
export function checkinBody(attempts: number, estServerTime: number, now: number): { estServerTime?: number } {
  return attempts > 0 || now - estServerTime > LIVE_CHECKIN_MS ? { estServerTime } : {};
}
